import React, { useEffect, useRef } from 'react';

const AsciiCube: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return;

        // Optimization: disable image smoothing
        ctx.imageSmoothingEnabled = false;

        const cubeSize = 120;
        const vertices = [
            [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
            [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]
        ].map(v => v.map(c => c * cubeSize));

        const edges = [
            [0, 1], [1, 2], [2, 3], [3, 0],
            [4, 5], [5, 6], [6, 7], [7, 4],
            [0, 4], [1, 5], [2, 6], [3, 7]
        ];

        // Simplified character sets
        const floatingChars = ['0', '1', '<', '>', '/', '|', '+', '-', '*', '#', '@', '~'];
        const edgeChar = '·';

        // Particles flying straight to center
        interface FloatingParticle {
            x: number;
            y: number;
            speed: number;
            size: number;
            char: string;
            baseOpacity: number;
            startDist: number;
        }

        const particles: FloatingParticle[] = [];
        const maxParticles = 30;
        const fadeStartDist = 120; // start fading at this distance from center

        let time = 0;
        let centerX = 0;
        let centerY = 0;

        // Pre-calculated sin/cos cache
        const sinCache: number[] = [];
        const cosCache: number[] = [];
        for (let i = 0; i < 628; i++) {
            sinCache[i] = Math.sin(i / 100);
            cosCache[i] = Math.cos(i / 100);
        }

        const fastSin = (a: number) => sinCache[Math.floor(((a % 6.28) + 6.28) % 6.28 * 100)] || Math.sin(a);
        const fastCos = (a: number) => cosCache[Math.floor(((a % 6.28) + 6.28) % 6.28 * 100)] || Math.cos(a);

        // Glitch state
        let glitchTimer = 0;
        let glitchActive = false;
        let glitchIntensity = 0;

        const resizeCanvas = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            centerX = canvas.width / 2;
            centerY = canvas.height / 2;
        };

        const rotatePoint = (p: number[], rx: number, ry: number, rz: number): number[] => {
            let x = p[0], y = p[1], z = p[2];

            const cosX = fastCos(rx), sinX = fastSin(rx);
            const y1 = y * cosX - z * sinX;
            const z1 = y * sinX + z * cosX;

            const cosY = fastCos(ry), sinY = fastSin(ry);
            const x2 = x * cosY + z1 * sinY;
            const z2 = -x * sinY + z1 * cosY;

            const cosZ = fastCos(rz), sinZ = fastSin(rz);
            const x3 = x2 * cosZ - y1 * sinZ;
            const y3 = x2 * sinZ + y1 * cosZ;

            return [x3, y3, z2];
        };

        const project = (point: number[]): { x: number; y: number; scale: number } => {
            const scale = 500 / (point[2] + 400);
            return {
                x: centerX + point[0] * scale,
                y: centerY + point[1] * scale,
                scale
            };
        };

        // Spawn particle from edge (not corners)
        const spawnParticle = () => {
            if (particles.length >= maxParticles) return;

            let x: number, y: number;
            const margin = 30;
            const cornerMargin = 150; // avoid corners

            // Choose side: 0=top, 1=right, 2=bottom, 3=left
            const side = Math.floor(Math.random() * 4);

            switch (side) {
                case 0: // top edge (avoid corners)
                    x = cornerMargin + Math.random() * (canvas.width - cornerMargin * 2);
                    y = -margin;
                    break;
                case 1: // right edge
                    x = canvas.width + margin;
                    y = cornerMargin + Math.random() * (canvas.height - cornerMargin * 2);
                    break;
                case 2: // bottom edge
                    x = cornerMargin + Math.random() * (canvas.width - cornerMargin * 2);
                    y = canvas.height + margin;
                    break;
                default: // left edge
                    x = -margin;
                    y = cornerMargin + Math.random() * (canvas.height - cornerMargin * 2);
                    break;
            }

            const dx = centerX - x;
            const dy = centerY - y;
            const startDist = Math.sqrt(dx * dx + dy * dy);

            particles.push({
                x, y,
                speed: 1.5 + Math.random() * 1.5,
                size: 10 + Math.random() * 6,
                char: floatingChars[Math.floor(Math.random() * floatingChars.length)],
                baseOpacity: 0.4 + Math.random() * 0.3,
                startDist
            });
        };

        const animate = () => {
            time += 0.012;

            // Spawn particles
            if (Math.random() < 0.08) spawnParticle();

            // Glitch timing
            glitchTimer += 0.01;
            if (glitchTimer > 6 + Math.random() * 10) {
                glitchActive = true;
                glitchIntensity = 0.3 + Math.random() * 0.3;
                glitchTimer = 0;
            }
            if (glitchActive) {
                glitchIntensity -= 0.025;
                if (glitchIntensity <= 0) glitchActive = false;
            }

            // Clear
            ctx.fillStyle = '#0a0a0a';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Transform cube vertices
            const rx = time * 0.25;
            const ry = time * 0.4;
            const rz = time * 0.15;

            const transformed = vertices.map(v => rotatePoint(v, rx, ry, rz));
            const projected = transformed.map(v => project(v));

            // Draw particles (fly straight to center, fade as they approach)
            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];

                // Calculate direction to center
                const dx = centerX - p.x;
                const dy = centerY - p.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                // Remove when very close to center
                if (dist < 20) {
                    particles.splice(i, 1);
                    continue;
                }

                // Move directly towards center
                const dirX = dx / dist;
                const dirY = dy / dist;
                p.x += dirX * p.speed;
                p.y += dirY * p.speed;

                // Calculate opacity: fade in at start, fade out near center
                let opacity = p.baseOpacity;

                // Fade in during first part of journey
                const travelProgress = 1 - (dist / p.startDist);
                if (travelProgress < 0.1) {
                    opacity *= travelProgress / 0.1;
                }

                // Fade out as approaching center
                if (dist < fadeStartDist) {
                    opacity *= dist / fadeStartDist;
                }

                ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
                ctx.font = `${Math.floor(p.size)}px monospace`;
                ctx.fillText(p.char, p.x, p.y);
            }

            // Draw cube edges
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.font = '10px monospace';

            edges.forEach(([i, j]) => {
                const p1 = projected[i];
                const p2 = projected[j];
                const steps = 12;
                for (let s = 0; s <= steps; s++) {
                    const t = s / steps;
                    ctx.fillText(edgeChar, p1.x + (p2.x - p1.x) * t, p1.y + (p2.y - p1.y) * t);
                }
            });

            ctx.shadowBlur = 0;

            // Glitch effect
            if (glitchActive && glitchIntensity > 0.1) {
                ctx.fillStyle = `rgba(255, 255, 255, ${0.05 * glitchIntensity})`;
                for (let i = 0; i < 2; i++) {
                    const y = Math.random() * canvas.height;
                    ctx.fillRect(0, y, canvas.width, 2);
                }
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="w-full h-full"
            style={{ background: '#0a0a0a' }}
        />
    );
};

export default AsciiCube;
